// Structured-data (schema.org JSON-LD) builders for Smiles of Loudoun.
import { PRACTICE, DOCTORS, SERVICES, CATEGORIES } from "@/data/site";

const SITE_URL = "https://primelogichub.com";
const DEFAULT_IMAGE = `${SITE_URL}/site-assets/opening-home.webp`;
const LOGO = `${SITE_URL}/site-assets/logo.png`;

// Google Business Profile — fill via env for portable deployments.
// Get your Place ID here: https://developers.google.com/maps/documentation/places/web-service/place-id
const GOOGLE_PLACE_ID = process.env.REACT_APP_GOOGLE_PLACE_ID || "";
const GOOGLE_CID = process.env.REACT_APP_GOOGLE_CID || ""; // Google Maps CID for direct listing URL

// Rough geo for Leesburg, VA (city center — accurate enough for local SEO).
const GEO = { latitude: 39.1157, longitude: -77.5636 };

const GALLERY_IMAGES = [
  `${SITE_URL}/site-assets/opening-home.webp`,
  `${SITE_URL}/site-assets/office1.webp`,
  `${SITE_URL}/site-assets/office2.webp`,
  `${SITE_URL}/site-assets/office3.webp`,
  `${SITE_URL}/site-assets/office4.webp`,
  `${SITE_URL}/site-assets/office5.webp`,
];

/** Full DentalClinic / LocalBusiness graph */
export function dentalClinicSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": ["Dentist", "LocalBusiness", "MedicalBusiness"],
    "@id": `${SITE_URL}/#dentist`,
    name: PRACTICE.name,
    image: GALLERY_IMAGES,
    logo: LOGO,
    url: SITE_URL,
    telephone: PRACTICE.phone,
    priceRange: "$$",
    currenciesAccepted: "USD",
    paymentAccepted: "Cash, Check, Credit Card, FSA, HSA, CareCredit, Sunbit",
    address: {
      "@type": "PostalAddress",
      streetAddress: "430 Harrison Street SE",
      addressLocality: "Leesburg",
      addressRegion: "VA",
      postalCode: "20175",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: GEO.latitude,
      longitude: GEO.longitude,
    },
    hasMap: PRACTICE.mapUrl,
    areaServed: [
      { "@type": "City", name: "Leesburg" },
      { "@type": "City", name: "Ashburn" },
      { "@type": "City", name: "Broadlands" },
      { "@type": "City", name: "Belmont" },
      { "@type": "City", name: "Brambleton" },
      { "@type": "City", name: "Purcellville" },
      { "@type": "City", name: "Middleburg" },
      { "@type": "City", name: "Sterling" },
      { "@type": "City", name: "Herndon" },
      { "@type": "City", name: "Reston" },
    ],
    openingHoursSpecification: PRACTICE.hours
      .filter((h) => /\d/.test(h.t))
      .map((h) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: expandDay(h.d),
        opens: parseOpen(h.t),
        closes: parseClose(h.t),
      })),
    availableLanguage: PRACTICE.languages,
    sameAs: [
      "https://www.smilesofloudoun.com",
      "https://g.page/smiles-of-loudoun",
      ...(GOOGLE_CID ? [`https://maps.google.com/?cid=${GOOGLE_CID}`] : []),
    ],
    employee: DOCTORS.map((d) => ({
      "@type": "Person",
      name: d.name,
      jobTitle: d.title,
    })),
  };

  // Google Business Profile Place ID → identifier (helps Google connect the
  // schema to the actual listing for map / knowledge-panel rich results).
  if (GOOGLE_PLACE_ID) {
    schema.identifier = [
      {
        "@type": "PropertyValue",
        propertyID: "GooglePlaceID",
        value: GOOGLE_PLACE_ID,
      },
    ];
  }

  return schema;
}

/** WebSite root schema (helps sitelinks & search box) */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: "Smiles of Loudoun",
    inLanguage: ["en-US", "es-ES"],
    publisher: { "@id": `${SITE_URL}/#dentist` },
  };
}

/** BreadcrumbList for any page. */
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

/** Per-service schema (used on service detail pages) */
export function serviceSchema(service) {
  const category = CATEGORIES.find((c) => c.slug === service.category);
  return {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: service.title,
    description: service.short,
    procedureType: category ? category.title : undefined,
    bodyLocation: "Mouth",
    provider: { "@id": `${SITE_URL}/#dentist` },
    url: `${SITE_URL}/services/${service.slug}`,
  };
}

/** FAQPage schema built from service.faq array */
export function faqSchema(faqList) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqList.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** ItemList for the /services index page */
export function servicesListSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: SERVICES.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: s.title,
      url: `${SITE_URL}/services/${s.slug}`,
    })),
  };
}

/** Person schema per doctor */
export function doctorsSchema() {
  return DOCTORS.map((d) => ({
    "@context": "https://schema.org",
    "@type": "Physician",
    name: d.name,
    jobTitle: d.title,
    worksFor: { "@id": `${SITE_URL}/#dentist` },
    description: Array.isArray(d.bio) ? d.bio[0] : d.bio,
  }));
}

/** BlogPosting schema for a single post */
export function blogPostingSchema(post) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.publishedAt,
    image: post.cover.startsWith("http") ? post.cover : `${SITE_URL}${post.cover}`,
    author: {
      "@type": "Person",
      name: post.author,
      worksFor: { "@id": `${SITE_URL}/#dentist` },
    },
    publisher: { "@id": `${SITE_URL}/#dentist` },
    mainEntityOfPage: `${SITE_URL}/insights/${post.slug}`,
    keywords: (post.tags || []).join(", "),
  };
}

/** Blog index (ItemList) */
export function blogIndexSchema(posts) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${SITE_URL}/insights#blog`,
    name: "Smiles of Loudoun · Insights",
    url: `${SITE_URL}/insights`,
    publisher: { "@id": `${SITE_URL}/#dentist` },
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.publishedAt,
      url: `${SITE_URL}/insights/${p.slug}`,
    })),
  };
}

// ---------- helpers ----------

function expandDay(d) {
  const map = {
    Mon: "Monday", Monday: "Monday",
    Tue: "Tuesday", Tuesday: "Tuesday",
    Wed: "Wednesday", Wednesday: "Wednesday",
    Thu: "Thursday", Thursday: "Thursday",
    Fri: "Friday", Friday: "Friday",
    Sat: "Saturday", Saturday: "Saturday",
    Sun: "Sunday", Sunday: "Sunday",
  };
  if (d.includes("–") || d.includes("-")) {
    const [start, end] = d.split(/[–-]/).map((s) => s.trim());
    const order = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
    const s = order.indexOf(map[start] || start);
    const e = order.indexOf(map[end] || end);
    if (s >= 0 && e >= 0) return order.slice(s, e + 1);
  }
  return map[d.trim()] ? [map[d.trim()]] : [];
}

function parseOpen(t) {
  const m = t.match(/(\d{1,2}(?::\d{2})?)\s*(am|pm)?/i);
  return m ? to24h(m[1], m[2] || "am") : "09:00";
}
function parseClose(t) {
  const parts = t.split("–");
  if (parts.length < 2) return "17:00";
  const m = parts[1].match(/(\d{1,2}(?::\d{2})?)\s*(am|pm)?/i);
  return m ? to24h(m[1], m[2] || "pm") : "17:00";
}
function to24h(time, period) {
  let [h, m] = time.split(":");
  h = parseInt(h, 10);
  if (/pm/i.test(period) && h < 12) h += 12;
  if (/am/i.test(period) && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m || "00"}`;
}
